import { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertCircle, Sun, Plug, Zap, Wind, Wifi, ChevronDown, ChevronRight } from 'lucide-react';
import { chargeSlotAppearance } from '@/components/unit/SystemStatusIcons.helpers';
import { BatteryDirectionMark } from '@/components/unit/BatteryDirectionMark';
import { BatteryCriticalTag } from '@/components/unit/BatteryCriticalTag';
import { HierarchicalGroupNode, Unit, UserPermissions, IndustryType } from '@/types';
import { GroupDivider } from './GroupDivider';
import { useFilters } from '@/contexts/FilterContext';
import { useMap } from '@/contexts/MapContext';
import { cn, formatLocationShort } from '@/lib/utils';
import { getLocationTextClass } from '@/lib/locationUtils';
import { legacyIconState } from '@/lib/legacyStatusIcons';
import { MODEL_TYPE_MAPPING, INDUSTRY_TYPE_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import mapboxgl from 'mapbox-gl';

interface GroupedUnitListProps {
  units: Unit[];
  permissions: UserPermissions;
  selectedUnit: Unit | null;
  hoveredUnit?: Unit | null; // Unit being hovered on map
  onUnitClick: (unit: Unit) => void;
  onUnitHover?: (unit: Unit | null) => void; // Callback when hovering over unit in sidebar
  onCloseDetails?: () => void; // Optional callback to close unit details panel
  sortBy: 'status' | 'name' | 'battery' | 'systems' | 'solar' | 'charger' | 'hvac' | 'starlink';
  sortOrder: 'asc' | 'desc';
  allExpanded?: boolean; // Control expand/collapse state from parent
  onToggleAll?: () => void; // Callback to notify parent of toggle all action
  isMobile?: boolean; // Enable mobile-specific styling
}

// Constants for map zoom (matching ClusterContent pattern)
const ZOOM_THRESHOLDS = {
  MAX_AUTO_ZOOM: 15,
};

const BOUNDS_PADDING = {
  desktop: { top: 100, bottom: 100, left: 460, right: 100 }, // Extra left padding for sidebar
  mobile: { top: 100, bottom: 350, left: 50, right: 50 },
};

export function GroupedUnitList({
  units,
  permissions,
  selectedUnit,
  hoveredUnit,
  onUnitClick,
  onUnitHover,
  onCloseDetails,
  sortBy,
  sortOrder,
  allExpanded = false,
  onToggleAll: _onToggleAll,
  isMobile = false,
}: GroupedUnitListProps) {
  const {
    buildHierarchicalGroups,
    setSelectedGroupIds,
    setSelectedModels,
    setSelectedIndustryTypes,
    setSelectedCompanyIds,
    companies,
    hasSavedFilters,
    saveFiltersForRestore,
    shouldExpandAll,
    setShouldExpandAll
  } = useFilters();
  const { map } = useMap();

  // Build hierarchical groups first (needed for single group detection)
  const hierarchicalGroups = buildHierarchicalGroups(units, permissions);

  // Check if there's only one top-level group (superadmin filtered to single company)
  const hasSingleTopLevelGroup = hierarchicalGroups.length === 1;
  const singleGroupId = hasSingleTopLevelGroup ? hierarchicalGroups[0].id : null;

  // Check if user belongs to / is viewing a single company
  // For non-superadmins, the hierarchy skips the company level and shows
  // productions as top-level nodes, so hasSingleTopLevelGroup is false even
  // with one company. Use the companies list as the source of truth.
  const isSingleCompany = companies.length === 1 || hasSingleTopLevelGroup;

  // Helper to collect all group IDs in the hierarchy tree
  const collectAllGroupIds = (nodes: HierarchicalGroupNode[]): Set<string> => {
    const ids = new Set<string>();
    const collect = (n: HierarchicalGroupNode[]) => {
      n.forEach(node => {
        ids.add(node.id);
        if (node.children) collect(node.children);
      });
    };
    collect(nodes);
    return ids;
  };

  // State for tracking which groups are expanded
  // Initialize with a function to compute initial state once
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Single company: expand entire tree so all units are visible
    if (isSingleCompany && hierarchicalGroups.length > 0) {
      return collectAllGroupIds(hierarchicalGroups);
    }
    return new Set<string>();
  });

  // State for tracking which industry sections are collapsed (superadmin only)
  // All industries start expanded by default
  const [collapsedIndustries, setCollapsedIndustries] = useState<Set<IndustryType>>(new Set());

  // Toggle collapse state for an industry section
  const toggleIndustry = useCallback((industry: IndustryType) => {
    setCollapsedIndustries(prev => {
      const next = new Set(prev);
      if (next.has(industry)) {
        next.delete(industry);
      } else {
        next.add(industry);
      }
      return next;
    });
  }, []);

  // For superadmins, get the industry type for a company-level node from its units
  const getIndustryForCompany = useCallback((group: HierarchicalGroupNode): IndustryType => {
    // Use the most common industry type from units, defaulting to 'film'
    if (group.units.length === 0) return 'film';
    const industryCount: Record<string, number> = {};
    group.units.forEach(unit => {
      const industry = unit.industry_type || 'film';
      industryCount[industry] = (industryCount[industry] || 0) + 1;
    });
    // Find the most common industry
    let maxCount = 0;
    let primaryIndustry: IndustryType = 'film';
    Object.entries(industryCount).forEach(([industry, count]) => {
      if (count > maxCount) {
        maxCount = count;
        primaryIndustry = industry as IndustryType;
      }
    });
    return primaryIndustry;
  }, []);

  // Group hierarchical nodes by industry type (for superadmins only)
  const groupedByIndustry = useMemo(() => {
    if (!permissions.canSeeCompanies) return null;

    const grouped: Record<IndustryType, HierarchicalGroupNode[]> = {
      film: [], event: [], gov: [], utility: [], relief: []
    };

    hierarchicalGroups.forEach(group => {
      if (group.level === 'company') {
        const industry = getIndustryForCompany(group);
        grouped[industry].push(group);
      } else {
        // Non-company level groups go to 'film' by default
        grouped['film'].push(group);
      }
    });

    return grouped;
  }, [hierarchicalGroups, permissions.canSeeCompanies, getIndustryForCompany]);

  // Track previous allExpanded value to detect prop changes
  const [prevAllExpanded, setPrevAllExpanded] = useState(allExpanded);

  // Effect to expand all groups when zoom-to filtering is triggered
  useEffect(() => {
    if (shouldExpandAll && hierarchicalGroups.length > 0) {
      const allIds = new Set<string>();
      const collectIds = (nodes: HierarchicalGroupNode[]) => {
        nodes.forEach(node => {
          allIds.add(node.id);
          if (node.children) collectIds(node.children);
        });
      };
      collectIds(hierarchicalGroups);
      setExpandedGroups(allIds);

      // For superadmins: also expand all industry dividers
      if (permissions.canSeeCompanies) {
        setCollapsedIndustries(new Set());
      }

      setShouldExpandAll(false);
    }
  }, [shouldExpandAll, hierarchicalGroups, permissions.canSeeCompanies]);

  // Auto-expand entire tree for single company when data loads after initial empty render
  // Handles the case where units arrive AFTER component mount
  // (useState initial function only runs once, so we need this effect)
  // Track hierarchicalGroups.length so the effect re-fires when groups populate
  const hierarchicalGroupsCount = hierarchicalGroups.length;
  useEffect(() => {
    if (isSingleCompany && hierarchicalGroups.length > 0) {
      setExpandedGroups(collectAllGroupIds(hierarchicalGroups));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally exclude expandedGroups to prevent infinite loop
  }, [isSingleCompany, singleGroupId, companies.length, hierarchicalGroupsCount]);

  // Only sync expanded state when allExpanded prop actually changes
  // This avoids the infinite loop from hierarchicalGroups reference changes
  if (prevAllExpanded !== allExpanded) {
    setPrevAllExpanded(allExpanded);

    if (allExpanded) {
      // Expand all groups
      const allGroupIds = new Set<string>();
      const collectIds = (nodes: HierarchicalGroupNode[]) => {
        nodes.forEach(node => {
          allGroupIds.add(node.id);
          if (node.children) {
            collectIds(node.children);
          }
        });
      };
      collectIds(hierarchicalGroups);
      setExpandedGroups(allGroupIds);

      // For superadmins: also expand all industry dividers
      if (permissions.canSeeCompanies) {
        setCollapsedIndustries(new Set());
      }
    } else {
      // Collapse all groups — user explicitly requested collapse
      setExpandedGroups(new Set<string>());

      // For superadmins: collapse all industry dividers (roll up to just industry headers)
      if (permissions.canSeeCompanies) {
        setCollapsedIndustries(new Set(INDUSTRY_TYPE_ORDER));
      }
    }
  }

  // Collect descendant IDs that have children (groups/sites only, not leaf nodes)
  // Leaf nodes (no children) are excluded so expanding all shows the tree structure
  // without flooding the list with individual units
  const collectNonLeafDescendantIds = useCallback((node: HierarchicalGroupNode): string[] => {
    const ids: string[] = [];
    const collect = (n: HierarchicalGroupNode) => {
      if (n.children) {
        n.children.forEach(child => {
          // Only include nodes that themselves have children (non-leaf)
          // Leaf nodes are left collapsed so their units stay hidden
          if (child.children && child.children.length > 0) {
            ids.push(child.id);
            collect(child);
          }
          // Always include the child ID so the parent's children list is visible,
          // but don't recurse into leaf nodes
          if (!child.children || child.children.length === 0) {
            ids.push(child.id);
          }
        });
      }
    };
    collect(node);
    return ids;
  }, []);

  // Toggle expand/collapse for a group
  // When collapsing, also clear all descendant IDs so re-expanding
  // shows children in their collapsed state (no units listed)
  const toggleGroup = useCallback((node: HierarchicalGroupNode) => {
    // Prevent collapsing the single top-level group
    if (hasSingleTopLevelGroup && node.id === singleGroupId) {
      return; // Do nothing - keep it expanded
    }

    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) {
        // Collapsing: remove this node AND all descendants
        next.delete(node.id);
        collectNonLeafDescendantIds(node).forEach(id => next.delete(id));
      } else {
        // Expanding: just open this level
        next.add(node.id);
      }
      return next;
    });
  }, [hasSingleTopLevelGroup, singleGroupId, collectNonLeafDescendantIds]);

  // Check if all non-leaf descendants of a node are expanded
  const checkAllDescendantsExpanded = useCallback((node: HierarchicalGroupNode): boolean => {
    const descendantIds = collectNonLeafDescendantIds(node);
    if (descendantIds.length === 0) return false;
    return descendantIds.every(id => expandedGroups.has(id));
  }, [collectNonLeafDescendantIds, expandedGroups]);

  // Toggle expand/collapse for all non-leaf descendants of a node
  // Expands groups/sites so you can see the tree structure, but leaves
  // leaf nodes collapsed so units aren't shown until the user clicks in
  const toggleAllDescendants = useCallback((node: HierarchicalGroupNode) => {
    const descendantIds = collectNonLeafDescendantIds(node);
    if (descendantIds.length === 0) return;

    const allExpanded = descendantIds.every(id => expandedGroups.has(id));

    setExpandedGroups(prev => {
      const next = new Set(prev);
      // Always ensure the node itself is expanded so the user can see results
      next.add(node.id);
      if (allExpanded) {
        // Collapse all descendants
        descendantIds.forEach(id => next.delete(id));
      } else {
        // Expand all descendants (groups/sites only)
        descendantIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [collectNonLeafDescendantIds, expandedGroups]);

  // Zoom map to group bounds and update filter
  const zoomToGroup = useCallback((group: HierarchicalGroupNode) => {
    if (!map || group.units.length === 0) return;

    // Close unit details panel if open
    if (onCloseDetails) {
      onCloseDetails();
    }

    // Save current filters before applying new selection (only if not already saved)
    if (!hasSavedFilters) {
      saveFiltersForRestore();
    }

    // Filter to units with real GPS coordinates
    const unitsWithLocation = group.units.filter(t => t.location && !t.location.location_missing);

    const bounds = new mapboxgl.LngLatBounds();
    unitsWithLocation.forEach(unit => {
      bounds.extend([unit.location.lng, unit.location.lat]);
    });

    // Collect all relevant group IDs
    const groupIds: string[] = [];
    
    if (group.siteId) {
      // Site level: just select this site
      groupIds.push(group.siteId);
    } else if (group.groupId) {
      // Group level: select group AND all child sites
      groupIds.push(group.groupId);
      
      // Find all child sites for this group
      if (group.children && group.children.length > 0) {
        group.children.forEach(child => {
          if (child.siteId) {
            groupIds.push(child.siteId);
          }
        });
      }
    } else if (group.companyId) {
      // Company level: select all groups and sites under this company
      if (group.children && group.children.length > 0) {
        group.children.forEach(childGroup => {
          if (childGroup.groupId) {
            groupIds.push(childGroup.groupId);
          }
          if (childGroup.children && childGroup.children.length > 0) {
            childGroup.children.forEach(site => {
              if (site.siteId) {
                groupIds.push(site.siteId);
              }
            });
          }
        });
      }
    }

    // Determine unique industry types in this group
    const uniqueIndustryTypes = Array.from(
      new Set(group.units.map(t => t.industry_type).filter(Boolean))
    ) as IndustryType[];

    // Get ALL model codes for these industry types
    const relevantModelCodes = Object.entries(MODEL_TYPE_MAPPING)
      .filter(([_code, types]) => types.some(type => uniqueIndustryTypes.includes(type as IndustryType)))
      .map(([code, _types]) => code);

    // Determine company ID for this group/site
    const companyId = group.companyId || group.units[0]?.company_id;

    // Update filter selection
    if (groupIds.length > 0) {
      setSelectedGroupIds(groupIds);
    }
    if (uniqueIndustryTypes.length > 0) {
      setSelectedIndustryTypes(uniqueIndustryTypes);
    }
    setSelectedModels(relevantModelCodes);

    // Select only the relevant company (fixes visual state mismatch in company dropdown)
    if (companyId) {
      setSelectedCompanyIds([companyId]);
    }

    // Trigger expand-all so filtered hierarchy is fully visible
    setShouldExpandAll(true);

    // Zoom map (only if we have units with real GPS)
    if (unitsWithLocation.length > 0) {
      const isMobile = window.innerWidth < 768;
      const paddingOptions = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;

      map.fitBounds(bounds, {
        padding: paddingOptions,
        duration: 800,
        maxZoom: ZOOM_THRESHOLDS.MAX_AUTO_ZOOM,
        essential: true,
      });
    }
  }, [map, setSelectedGroupIds, setSelectedIndustryTypes, setSelectedModels, setSelectedCompanyIds, hasSavedFilters, saveFiltersForRestore, onCloseDetails]);

  // Sort units within a group
  const sortUnits = useCallback((groupUnits: Unit[]) => {
    const sorted = [...groupUnits].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'status': {
          const statusPriority: Record<string, number> = {
            'online': 1,
            'offline': 2,
            'idle': 3,
            'unknown': 4
          };
          const aPriority = statusPriority[a.status] || 99;
          const bPriority = statusPriority[b.status] || 99;
          comparison = aPriority - bPriority;
          break;
        }
        case 'name':
          comparison = a.unit_code.localeCompare(b.unit_code);
          break;
        case 'battery': {
          // The number the row shows (live_status); a trailer with no reading sorts last either way.
          const batteryA = legacyIconState(a.live_status).batteryLevel;
          const batteryB = legacyIconState(b.live_status).batteryLevel;
          const knownA = batteryA != null;
          const knownB = batteryB != null;
          if (knownA !== knownB) return knownA ? -1 : 1;
          if (batteryA == null || batteryB == null) return 0;
          comparison = batteryA - batteryB;
          break;
        }
        case 'solar':
          comparison = ((a.systems?.solar?.generation ?? 0) > 0 ? 1 : 0) - ((b.systems?.solar?.generation ?? 0) > 0 ? 1 : 0);
          break;
        case 'charger':
          comparison = (a.systems?.charger?.enabled ? 1 : 0) - (b.systems?.charger?.enabled ? 1 : 0);
          break;
        case 'hvac':
          comparison = (a.systems?.hvac?.enabled ? 1 : 0) - (b.systems?.hvac?.enabled ? 1 : 0);
          break;
        case 'starlink':
          comparison = (a.systems?.starlink?.connectionStatus === 'online' ? 1 : 0) - (b.systems?.starlink?.connectionStatus === 'online' ? 1 : 0);
          break;
        case 'systems': {
          const countA = [
            a.systems?.solar?.enabled,
            a.systems?.charger?.enabled,
            a.systems?.hvac?.enabled,
            a.systems?.starlink?.enabled,
          ].filter(Boolean).length;
          const countB = [
            b.systems?.solar?.enabled,
            b.systems?.charger?.enabled,
            b.systems?.hvac?.enabled,
            b.systems?.starlink?.enabled,
          ].filter(Boolean).length;
          comparison = countA - countB;
          break;
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [sortBy, sortOrder]);

  // Build ancestor chain for hovered unit (for highlight effect)
  const ancestorChain = useMemo(() => {
    if (!hoveredUnit) return new Set<string>();

    const chain = new Set<string>();

    // Walk up the hierarchy building chain
    const buildChainRecursively = (nodes: HierarchicalGroupNode[], targetUnitId: string): void => {
      for (const node of nodes) {
        // Check if this node contains the unit (directly or in descendants)
        const containsUnit = (n: HierarchicalGroupNode): boolean => {
          if (n.units.some(t => t.id === targetUnitId)) return true;
          if (n.children) {
            return n.children.some(child => containsUnit(child));
          }
          return false;
        };

        if (containsUnit(node)) {
          chain.add(node.id);
          // Recurse into children to mark entire ancestor path
          if (node.children) {
            buildChainRecursively(node.children, targetUnitId);
          }
        }
      }
    };

    buildChainRecursively(hierarchicalGroups, hoveredUnit.id);
    return chain;
  }, [hoveredUnit, hierarchicalGroups]);

  // Recursively render group nodes with proper indentation
  const renderGroupNode = (node: HierarchicalGroupNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedGroups.has(node.id);
    const sortedUnits = sortUnits(node.units);
    const isInAncestorChain = ancestorChain.has(node.id);
    const isOnlySingleTopLevel = hasSingleTopLevelGroup && level === 0;

    return (
      <div key={node.id}>
        <GroupDivider
          group={node}
          isExpanded={isExpanded}
          onToggle={() => toggleGroup(node)}
          onZoomToGroup={() => zoomToGroup(node)}
          onToggleAllDescendants={() => toggleAllDescendants(node)}
          areAllDescendantsExpanded={checkAllDescendantsExpanded(node)}
          hasChildren={!!(node.children && node.children.length > 0)}
          indentLevel={level}
          isMobile={isMobile}
          isHighlighted={isInAncestorChain}
          disableCollapse={isOnlySingleTopLevel}
        />

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
            >
              {/* Render children groups first */}
              {node.children && node.children.length > 0 && (
                <div>
                  {node.children.map(child => renderGroupNode(child, level + 1))}
                </div>
              )}

              {/* Render units if this is a leaf node (no children) or if it has direct units */}
              {(!node.children || node.children.length === 0) && sortedUnits.map(unit => {
                // The original icons, lit from the server's live_status (Eric, 2026-10-30: "Put the
                // old icons back exactly"); App's band colours the pill, "—" for no reading.
                const { batteryLevel, batteryFill, direction, critical, solarOn, plug, chargerOn, hvacOn, starlinkOn } = legacyIconState(unit.live_status);

                return (
                  <button
                    key={unit.id}
                    data-unit-id={unit.id}
                    onClick={() => onUnitClick(unit)}
                    onMouseEnter={() => onUnitHover?.(unit)}
                    className={cn(
                      'w-full flex items-center gap-3 border-b border-border/50 transition-all text-left',
                      isMobile ? 'px-3 py-3.5 active:bg-accent/50' : 'px-4 py-3 hover:bg-accent/50',
                      selectedUnit?.id === unit.id && 'bg-accent/50',
                      hoveredUnit?.id === unit.id && selectedUnit?.id !== unit.id && 'bg-[#8FB560]/10 dark:bg-[#8FB560]/15'
                    )}
                  >
                    {/* Status Indicator */}
                    <div className="w-6 flex justify-center flex-shrink-0">
                      <div className={cn(
                        "w-2.5 h-2.5 rounded-full",
                        unit.status === 'online' && "bg-success",
                        unit.status === 'offline' && "bg-error",
                        unit.status === 'idle' && "bg-gray-400",
                        unit.status === 'unknown' && "bg-gray-300"
                      )} />
                    </div>

                    {/* Unit Name & ID */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium">{unit.unit_code}</span>
                        {/* Dual alert icons: warning (triangle) and fault (circle) */}
                        {(unit.warning_count ?? 0) > 0 && (
                          <AlertTriangle className="h-3 w-3 flex-shrink-0 text-warning" />
                        )}
                        {(unit.fault_count ?? 0) > 0 && (
                          <AlertCircle className="h-3 w-3 flex-shrink-0 text-fault" />
                        )}
                      </div>
                      <div className={cn('text-[10px] text-muted-foreground/60 truncate', getLocationTextClass(unit.locationSource))}>
                        {formatLocationShort(unit.location.address)}
                      </div>
                    </div>

                    {/* Battery Indicator */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* At 15% or less the Critical word sits under the reading, so the row
                          keeps room for the unit code on a phone. */}
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-medium text-muted-foreground min-w-[22px] text-right">{batteryLevel == null ? '—' : `${batteryLevel}%`}</span>
                          <BatteryDirectionMark direction={direction} fill={batteryFill} />
                        </div>
                        <BatteryCriticalTag show={critical} />
                      </div>
                      <div className="w-2.5 h-5 bg-background/50 rounded-sm border border-border/50 flex flex-col-reverse overflow-hidden">
                        <div
                          className={cn('w-full transition-all', batteryFill)}
                          style={{ height: `${batteryLevel ?? 0}%` }}
                        />
                      </div>
                    </div>

                    {/* System Icons */}
                    <div className={cn("flex items-center flex-shrink-0", isMobile ? "gap-1" : "gap-2")}>
                      {/* On shore power the Alerts board's plug takes the sun's spot (Eric: "yes plug"). */}
                      {plug ? (
                        <Plug className={cn('h-4 w-4', chargeSlotAppearance(plug).colorClass)} />
                      ) : (
                        <Sun
                          className={cn(
                            'h-4 w-4',
                            solarOn ? 'text-amber-400' : 'text-muted-foreground/30'
                          )}
                        />
                      )}
                      <Zap
                        className={cn(
                          'h-4 w-4',
                          chargerOn ? 'text-blue-400' : 'text-muted-foreground/30'
                        )}
                      />
                      <Wind
                        className={cn(
                          'h-4 w-4',
                          hvacOn ? 'text-cyan-400' : 'text-muted-foreground/30'
                        )}
                      />
                      <Wifi
                        className={cn(
                          'h-4 w-4',
                          starlinkOn ? 'text-green-400' : 'text-muted-foreground/30'
                        )}
                      />
                    </div>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Render industry section header for superadmins
  const renderIndustryHeader = (industry: IndustryType, count: number) => {
    const isCollapsed = collapsedIndustries.has(industry);
    return (
      <button
        key={`industry-header-${industry}`}
        onClick={() => toggleIndustry(industry)}
        className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border/50 hover:bg-muted/70 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {INDUSTRY_TYPE_LABELS[industry] || industry.toUpperCase()}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          ({count})
        </span>
      </button>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Superadmin view: Group companies by industry with collapsible sections */}
      {permissions.canSeeCompanies && groupedByIndustry ? (
        <>
          {INDUSTRY_TYPE_ORDER.map(industry => {
            const industryGroups = groupedByIndustry[industry];
            // Hide industry section if no matching companies
            if (!industryGroups || industryGroups.length === 0) return null;

            const isCollapsed = collapsedIndustries.has(industry);
            const totalUnits = industryGroups.reduce((sum, g) => sum + g.unitCount, 0);

            return (
              <div key={industry}>
                {renderIndustryHeader(industry, totalUnits)}
                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {industryGroups.map(group => renderGroupNode(group, 0))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </>
      ) : (
        /* Regular view: Show hierarchy directly */
        hierarchicalGroups.map(group => renderGroupNode(group, 0))
      )}

      {hierarchicalGroups.length === 0 && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          No units found
        </div>
      )}
    </div>
  );
}
